/**
 * The load demand of a resident viewport: which root rows a renderer loads
 * for the rows it holds resident
 * (`RowViewport.Selection`), and what it may leave unloaded.
 *
 * ## The contract
 *
 * A bind of `range` for a resident set `R` is:
 *
 * 1. the sheet-wide loads, once per bind — `startFetch(range)` (the cell
 *    accessor's bulk fetch), `ensureRowsLoaded(range)` (the rows-side
 *    sheet's batched prefetch) and `cells.ensureLoaded(range)`. Each is one
 *    sheet-wide fetcher: a covered range issues nothing, an uncovered one
 *    one request for the missing slice. None of them is a per-row fetch, so
 *    none scales with the number of rows;
 * 2. `row.ensureLoaded(range, null)` for each root row in
 *    `RowDemand.roots(sheet, R)` — and for no other root row.
 *
 * `roots` answers, in root (bind) order, the root ancestor of every resident
 * row (a root row is its own). That is complete for the resident rows: a root
 * row's `ensureLoaded` is one load pass over its whole tree, which also loads
 * the VALUES of every row a formula in that tree reads, wherever it sits
 * (`../rows/row-load.ts`), and an aggregate's batch covers its constituents.
 * So a resident child, a formula's inputs, an aggregate's constituents and
 * a header/footer surface row (resident as "sticky") load exactly as an
 * unvirtualized bind loads them. Ids the sheet does not hold, or that do not
 * reach a root, demand nothing.
 *
 * ## Offscreen freshness
 *
 * A root row outside the demand is not loaded for the new range. What it
 * already holds stays: invalidations (sync pushes, local writes, formula
 * recomputes) keep updating cached cells and indices sheet-wide whether or
 * not a row is resident, and nothing it holds is dropped or marked stale by
 * leaving residency. When it becomes resident again — a scroll, a resize, a
 * pin — the next bind demands it and its fetcher requests only the part of
 * the range it does not cover. Renderers recompute the demand on every bind
 * (every residency change), never cache it across one.
 *
 * Cost: one parent walk per resident row, bounded by its depth.
 *
 * @module
 */
import type { Option } from "../core/option.ts";
import type { RowLocalId } from "../ids/row-local-id.ts";

/** Which root rows a resident viewport loads (see the module doc for the contract). */
export namespace RowDemand {
  /** The sheet reads the demand is derived from; a sheet's reads (`platformOf(handle).sheet`) are one. */
  export interface Sheet {
    /** The top-level rows, in tree order (`SheetModelReads.rootRows`). */
    readonly rootRows: readonly { readonly id: RowLocalId }[];
    /** A row's parent; unset for a root or an unknown row (`SheetModelReads.parentOf`). */
    parentOf(id: RowLocalId): Option<RowLocalId>;
  }

  /**
   * The root rows whose `ensureLoaded` the `resident` rows need, in root
   * order, each once: the root ancestor of each resident row.
   */
  export function roots(sheet: Sheet, resident: Iterable<RowLocalId>): readonly RowLocalId[] {
    // `rootRows`, not `rootIds()`: a sheet with no tree yet answers its row
    // list as its roots, and a bind walks `rootRows`.
    const rootIds = sheet.rootRows.map((row) => row.id);
    const isRoot = new Set<RowLocalId>(rootIds);
    const needed = new Set<RowLocalId>();
    for (const id of resident) {
      const root = rootOf(sheet, isRoot, id);
      if (root != null) needed.add(root);
    }
    return Object.freeze(rootIds.filter((id) => needed.has(id)));
  }
}

/** `id`'s root ancestor (itself for a root); `null` for an id that reaches no root (unknown, detached, or a cycle). */
function rootOf(sheet: RowDemand.Sheet, isRoot: ReadonlySet<RowLocalId>, id: RowLocalId): Option<RowLocalId> {
  const seen = new Set<RowLocalId>();
  let at: Option<RowLocalId> = id;
  while (at != null && !seen.has(at)) {
    if (isRoot.has(at)) return at;
    seen.add(at);
    at = sheet.parentOf(at);
  }
  return null;
}
